import { Search, Settings } from '.generated/Feature.CW.model';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import React, { useImperativeHandle, useState, forwardRef } from 'react';
import { defaultConfig, tv } from 'tailwind-variants';
import {
  buildSearchBox,
  buildResultList,
  Result,
  buildInteractiveResult,
  SearchEngine,
  InteractiveResult,
  Suggestion,
} from '@coveo/headless';
import {
  CoveoEngineProvider,
  useCoveoEngine,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import Pagination from 'helpers/Coveo/Pagination';
import QuerySummary from 'helpers/Coveo/QuerySummary';
import { getPageTypeByTemplateId } from 'lib/utils/getPageTypeByTemplateId';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { getAddress } from '../content/LocationHeader';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import classNames from 'classnames';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { useRouter } from 'next/router';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import UrlManagerController from 'helpers/Coveo/UrlManagerController';
import useIsEditing from 'lib/hooks/useIsEditing';
import NoResultsMessage from 'helpers/Coveo/NoResultsMessage';
import { formatDate } from 'lib/utils/utilityFunction';

type PageSettings = Settings.SiteSettings['fields'];
type GlobalSearch = Search.GlobalSearch_Component;

defaultConfig.twMerge = false;

const GlobalSearch = (props: GlobalSearch) => {
  const { sitecoreContext } = useSitecoreContext();
  const [isConfigReady, setIsConfigReady] = React.useState(false);
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;
  const [coveoConfig, setCoveoConfig] = React.useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
  });
  const [userCoordinates, setUserCoordinates] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const getUsertLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setUserCoordinates({ latitude: lat, longitude: lng });
      },
      (error) => {
        console.warn('Geolocation error:', error.message);
      },
      {
        timeout: 5000,
        maximumAge: 60000,
      }
    );
  };

  React.useEffect(() => {
    getUsertLocation();
  }, []);

  React.useEffect(() => {
    if (
      contextWithSettings?.globalSearchAccessToken?.value &&
      contextWithSettings?.globalSearchSearchHub?.value
    ) {
      setCoveoConfig({
        searchHub: contextWithSettings.globalSearchSearchHub.value,
        pipeline: contextWithSettings.globalSearchPipeline?.value || '',
        accessToken: contextWithSettings.globalSearchAccessToken.value,
        organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
      });
      setIsConfigReady(true);
    }
  }, [contextWithSettings]);

  // Prevent rendering unless config is complete
  if (!isConfigReady) {
    return null;
  }

  return (
    <CoveoEngineProvider
      key={JSON.stringify(userCoordinates)}
      config={coveoConfig}
      useDistanceSorting={true}
      userCoordinates={userCoordinates ?? undefined}
    >
      <SearchComponent {...props} />
      <UrlManagerController />
    </CoveoEngineProvider>
  );
};

const SearchComponent = (props: GlobalSearch) => {
  const { base, headingStyle, titleLinkWrapperStyle, containerStyle } = TAILWIND_VARIANTS();
  const router = useRouter();
  const query = typeof router.query.q === 'string' ? router.query.q : '';
  const engine = useCoveoEngine();
  const isEditing = useIsEditing();
  const [firstSearchExecuted, setFirstSearchExecuted] = useState<boolean>(false);

  // React.useEffect(() => {
  //   // On first load, fetch all data without a query
  //   if (engine) {
  //     engine.executeFirstSearch();
  //   }
  // }, []);
  React.useEffect(() => {
    if (!engine) return;
    // Trigger initial search after all controllers (e.g., facets) are mounted
    let searchExecutedTimeout: NodeJS.Timeout;
    const searchTimeout: NodeJS.Timeout = setTimeout(() => {
      engine.executeFirstSearch();
    }, 100);

    const unsubscribe = engine.subscribe(() => {
      // Clear any existing timeout to prevent multiple state updates
      if (searchExecutedTimeout) {
        clearTimeout(searchExecutedTimeout);
      }
      // Only set the state if we have results or if it's the initial search
      if (engine.state.search.results.length > 0) {
        setFirstSearchExecuted(true);
      } else {
        searchExecutedTimeout = setTimeout(() => {
          setFirstSearchExecuted(true);
        }, 500);
      }
    });

    // Cleanup function to clear timeouts and unsubscribe
    return () => {
      unsubscribe();
      if (searchTimeout) clearTimeout(searchTimeout);
      if (searchExecutedTimeout) clearTimeout(searchExecutedTimeout);
    };
  }, []);

  return (
    <>
      <section className={classNames(base(), 'component component-theme theme-white py-[64px]')}>
        <div className={containerStyle()}>
          <div className={titleLinkWrapperStyle()}>
            <h1 className={headingStyle()}>
              <PlainTextWrapper field={props?.fields?.title} />
            </h1>
            {((props?.fields?.link1?.value?.href && props?.fields?.link2?.value?.href) ||
              isEditing) && (
              <div className="flex flex-col md:flex-row gap-x-[38px] gap-y-2">
                <LinkWrapper
                  field={props?.fields?.link1}
                  ctaVariant="tertiary"
                  ctaIcon="arrow-right"
                  className="text-left w-fit py-7 whitespace-nowrap min-h-[48px] min-w-[48px] flex items-center justify-center focus:outline-2 focus:outline-blue-base rounded-md transition duration-150"
                />
                <LinkWrapper
                  field={props?.fields?.link2}
                  ctaVariant="tertiary"
                  ctaIcon="arrow-right"
                  className="text-left w-fit py-7 whitespace-nowrap min-h-[48px] min-w-[48px] flex items-center justify-center focus:outline-2 focus:outline-blue-base rounded-md transition duration-150"
                />
              </div>
            )}
          </div>
          {/* Search Bar */}
          <SearchBar
            engine={engine}
            placeholder={props?.fields?.placeholderText}
            className="mt-[40px]"
            initialValue={query}
          />
        </div>
      </section>

      <section className="component component-theme theme-white pt-12 md:pt-[64px] pb-[40px] md:max-w-[840px] w-full mx-auto px-10 md:px-10 coveo-search-list">
        <div className="md:py-6">
          <QuerySummary />
        </div>

        {/* Search Results */}
        <SearchResults engine={engine} firstSearchExecuted={firstSearchExecuted} />

        <div className="pagination-wrapper mt-[30px]">
          <Pagination />
        </div>
      </section>
    </>
  );
};

export interface LocationInputHandle {
  setValue: (value: string) => void;
  onSearchSubmit: () => void;
}

export interface SearchBarProps {
  initialValue?: string;
  engine: SearchEngine | null;
  placeholder: Field<string> | undefined;
  className?: string;
  designType?: 'headerSearch' | 'default';
  handleCommonSearchResult?: (result: string[]) => void;
  redirectUrl?: string | null;
  handleSearchToggle?: (isOpen: boolean) => void;
  autoSubmitOnSetValue?: boolean; // to trigger auto set Search for A-Z search condition and Treatment keyword Search
}

export const SearchBar = forwardRef<LocationInputHandle, SearchBarProps>(
  (
    {
      initialValue,
      engine,
      placeholder,
      className,
      designType = 'default',
      handleCommonSearchResult,
      redirectUrl,
      handleSearchToggle,
      autoSubmitOnSetValue = false,
    },
    ref
  ) => {
    const {
      searchInputWrapper,
      searchInput,
      searchClearButton,
      searchSuggestionsList,
      searchSuggestionItem,
      searchSuggestionItemActive,
      searchButton,
    } = TAILWIND_VARIANTS();
    const [inputValue, setInputValue] = useState(initialValue || '');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isConditionFocused, setIsConditionFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const { getDictionaryValue } = useDictionary();
    const suggestionsListRef = React.useRef<HTMLUListElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const router = useRouter();
    const isEditing = useIsEditing();
    const { currentScreenWidth } = useCurrentScreenType();

    useImperativeHandle(ref, () => ({
      setValue: (value: string) => {
        setInputValue(value);
        setIsConditionFocused(!!value);
        if (searchBoxController) {
          searchBoxController.updateText(value);
          if (autoSubmitOnSetValue) {
            searchBoxController.submit();
          }
        }
      },
      focus: () => {
        inputRef.current?.focus();
      },
      onSearchSubmit: () => {
        handleSearchClick();
      },
    }));

    const searchBoxController = React.useMemo(
      () =>
        engine
          ? buildSearchBox(engine, {
              options: {
                numberOfSuggestions: 5,
                highlightOptions: {
                  notMatchDelimiters: {
                    open: '<span class="font-normal">',
                    close: '</span>',
                  },
                  correctionDelimiters: {
                    open: '<i>',
                    close: '</i>',
                  },
                },
              },
            })
          : null,
      [engine]
    );

    // Memoized handlers to avoid unnecessary re-renders
    const handleSuggestionClick = React.useCallback(
      (suggestion: string) => {
        if (searchBoxController) {
          setInputValue(suggestion);
          searchBoxController.updateText(suggestion);
          searchBoxController.submit();
          setIsConditionFocused(true);
          setSuggestions([]);
          setShowSuggestions(false);
          setHighlightedIndex(-1);
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        }
      },
      [searchBoxController]
    );

    const handleInputChange = React.useCallback(
      (value: string) => {
        if (searchBoxController) {
          setInputValue(value);
          searchBoxController.updateText(value);
          searchBoxController.showSuggestions(); // Fetch suggestions
          setShowSuggestions(true);
          setHighlightedIndex(-1);
        }
      },
      [searchBoxController]
    );

    const handleSearchClick = React.useCallback(
      (suggestionOrEvent?: string | React.SyntheticEvent) => {
        const suggestion = typeof suggestionOrEvent === 'string' ? suggestionOrEvent : undefined;
        if (redirectUrl && router && router.push) {
          router.push(`${redirectUrl}?q=${suggestion || inputValue}`);
          handleSearchToggle?.(false);
        }
        if (searchBoxController) {
          searchBoxController.updateText(suggestion || inputValue);
          searchBoxController.submit();
          setShowSuggestions(false);
          setHighlightedIndex(-1);
        }
      },
      [searchBoxController, inputValue, redirectUrl, router, handleSearchToggle]
    );

    const handleFocus = React.useCallback(() => {
      if (designType === 'headerSearch' && inputValue === '') {
        setShowSuggestions(false);
      } else {
        setShowSuggestions(true);
        setIsConditionFocused(true);
      }
    }, []);

    // Delay hiding suggestions to allow click event to register
    const handleBlur = React.useCallback(() => {
      setTimeout(() => {
        setShowSuggestions(false);
        setIsConditionFocused(false);
        setHighlightedIndex(-1);
      }, 100);
    }, []);

    const handleClearClick = React.useCallback(() => {
      if (searchBoxController) {
        setInputValue('');
        setSuggestions([]);
        searchBoxController.updateText('');
        searchBoxController.submit();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        setIsConditionFocused(false);
      }
    }, [searchBoxController]);

    React.useEffect(() => {
      if (initialValue !== undefined) {
        setInputValue(initialValue);
        if (searchBoxController) {
          searchBoxController.updateText(initialValue);
          searchBoxController.submit();
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue]);

    // Subscribe to searchBoxController state changes
    React.useEffect(() => {
      if (!searchBoxController) return;
      searchBoxController.updateText('');
      const unsubscribe = searchBoxController.subscribe(() => {
        setInputValue(searchBoxController.state.value);
        setSuggestions(searchBoxController.state.suggestions.map((s) => s));
        handleCommonSearchResult?.(searchBoxController.state.suggestions.map((s) => s.rawValue));
      });
      return () => {
        unsubscribe?.();
      };
    }, [searchBoxController]);

    // Keyboard navigation for suggestions
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
          handleBlur();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex((prev: number) =>
            suggestions.length === 0 ? -1 : (prev + 1) % suggestions.length
          );
          setShowSuggestions(true);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex((prev) =>
            suggestions.length === 0 ? -1 : (prev - 1 + suggestions.length) % suggestions.length
          );
          setShowSuggestions(true);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (showSuggestions && highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleSuggestionClick(suggestions[highlightedIndex].rawValue);
          } else {
            handleSearchClick();
          }
        }
      },
      [
        handleBlur,
        handleSearchClick,
        handleSuggestionClick,
        highlightedIndex,
        showSuggestions,
        suggestions,
      ]
    );

    const handleOptionClick = React.useCallback(
      (suggestion: string) => {
        setInputValue(suggestion);
        return designType === 'headerSearch'
          ? handleSearchClick(suggestion)
          : handleSuggestionClick(suggestion);
      },
      [designType, handleSearchClick, handleSuggestionClick]
    );

    // Scroll suggestion into view when highlightedIndex changes
    React.useEffect(() => {
      if (
        suggestionsListRef.current &&
        highlightedIndex >= 0 &&
        highlightedIndex < suggestions.length
      ) {
        const el = suggestionsListRef.current.children[highlightedIndex] as HTMLElement;
        el?.scrollIntoView({ block: 'nearest' });
      }
    }, [highlightedIndex, suggestions]);

    return (
      <div
        className={classNames(
          className,
          designType === 'headerSearch' && currentScreenWidth < 1024
            ? 'gap-0 flex'
            : 'gap-5 md:flex'
        )}
      >
        <div className={searchInputWrapper()}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={classNames(
              searchInput(),
              designType !== 'headerSearch'
                ? `pb-5 pt-[30px] px-12 rounded-l-sm rounded-r-sm md:rounded-r-none `
                : `px-8 py-7  ${currentScreenWidth < 1024 ? 'rounded-l-sm' : 'rounded-sm'}`
            )}
            autoComplete="off"
            id="locationInput"
            aria-autocomplete="list"
            aria-controls="search-suggestions-list"
            aria-activedescendant={
              highlightedIndex >= 0 ? `search-suggestion-${highlightedIndex}` : undefined
            }
            role="combobox"
            aria-expanded={showSuggestions}
            placeholder={designType === 'headerSearch' ? placeholder?.value : ''}
            readOnly={isEditing}
          />
          {designType !== 'headerSearch' && (
            <label
              htmlFor="locationInput"
              className={`absolute left-12 transition-all text-gray-900 duration-200 pointer-events-none ${
                inputValue || isConditionFocused
                  ? 'text-14 leading-16 top-[10px]'
                  : 'text-18 leading-28 top-[20px]'
              }`}
            >
              <PlainTextWrapper field={placeholder} />
            </label>
          )}

          {inputValue && (
            <button
              className={searchClearButton()}
              onClick={handleClearClick}
              aria-label="Clear input"
              type="button"
            >
              <SvgIcon icon="close" />
            </button>
          )}

          {showSuggestions && suggestions.length > 0 && (
            <ul
              ref={suggestionsListRef}
              id="search-suggestions-list"
              className={searchSuggestionsList()}
              role="listbox"
              aria-label="Search suggestions"
            >
              {suggestions.map((s, index) => (
                <li
                  key={s.rawValue}
                  id={`search-suggestion-${index}`}
                  className={`${searchSuggestionItem()} ${
                    highlightedIndex === index ? searchSuggestionItemActive() : ''
                  }`}
                  onKeyDown={(e: React.KeyboardEvent<HTMLLIElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchClick();
                    }
                  }}
                  onMouseDown={() => handleOptionClick(s.rawValue)}
                  onClick={() => handleOptionClick(s.rawValue)}
                  role="option"
                  aria-selected={highlightedIndex === index}
                  tabIndex={-1}
                >
                  <span dangerouslySetInnerHTML={{ __html: s.highlightedValue }} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {designType === 'headerSearch' && currentScreenWidth < 1024 ? (
          <ButtonWrapper
            ctaVariant="primary"
            className="!p-0 min-w-[56px] rounded-l-[0px] rounded-r-sm text-blue-base underline flex items-center gap-4 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSearchClick}
            disabled={isEditing}
            aria-label="Search"
          >
            <SvgIcon icon="search" size="lg" />
          </ButtonWrapper>
        ) : (
          <ButtonWrapper
            ctaVariant="primary"
            className={searchButton()}
            ctaIcon="search"
            ctaIconAlignment="left"
            text={getDictionaryValue('SearchButtonLabel')}
            onClick={handleSearchClick}
            disabled={isEditing}
            aria-label="Search"
          />
        )}
      </div>
    );
  }
);
SearchBar.displayName = 'SearchBar';

const SearchResults = ({
  engine,
  firstSearchExecuted,
}: {
  engine: SearchEngine | null;
  firstSearchExecuted: boolean;
}) => {
  const { resultListItem, resultTitle, resultDescription } = TAILWIND_VARIANTS();
  const [results, setResults] = React.useState<Result[]>([]);

  const fieldsToInclude = [
    // General fields
    'cwtemplateid',
    'cwpagetype',
    'cwtitle',
    'cwdescription',
    // Provider data fields
    'cwfirstname',
    'cwlastname',
    'cwdegreetitles',
    'cwspecialties',
    'cwacceptingnewpatients',
    'cwprimarylocationtitle',
    'cwprimarylocationaddress',
    'cwprimarylocationaddressmapurl',
    // Location data fields
    'cwdegreetitles',
    'cwsearchspecialties',
    'cwaddress1',
    'cwcity',
    'cwstate',
    'cwzip',
    'cwaddressmapurl',
    'cwaddressmaptext',
    // blog data fields
    'cwheroimage',
    'cwblogdate',
    // Provider data fields & Location data fields
    'cwphoto',
    'clickUri',
    'distanceinmiles',
  ];

  const isLocationPageType = (pageType: string | undefined): boolean => {
    return (
      pageType === 'location' ||
      pageType === 'hospital' ||
      pageType === 'mentalhealth' ||
      pageType === 'primarycare' ||
      pageType === 'specialtycare' ||
      pageType === 'urgentcare'
    );
  };

  const resultListController = React.useMemo(
    () =>
      engine
        ? buildResultList(engine, {
            options: {
              fieldsToInclude: fieldsToInclude,
            },
          })
        : null,
    [engine]
  );

  React.useEffect(() => {
    const unsubscribeResultList =
      resultListController &&
      resultListController.subscribe(() => {
        setResults(resultListController.state.results);
      });

    return () => {
      unsubscribeResultList?.();
    };
  }, []);

  return (
    <ul>
      {firstSearchExecuted ? (
        results.length > 0 ? (
          results?.map((result: Result) => {
            const pageType = getPageTypeByTemplateId(result.raw.cwtemplateid as string);
            const isLocationPage = isLocationPageType(pageType);
            const controller =
              engine &&
              buildInteractiveResult(engine, {
                options: { result: result },
              });
            return (
              <li key={result.uniqueId} className={resultListItem()}>
                <article aria-labelledby={`result-title-${result.uniqueId}`}>
                  {isLocationPage ? (
                    <LocationContent result={result} controller={controller} />
                  ) : pageType === 'provider' ? (
                    <ProviderContent result={result} controller={controller} />
                  ) : pageType === 'blog-details' ? (
                    <BlogContent result={result} controller={controller} />
                  ) : (
                    <>
                      {(result.raw.cwpagetype as string) && (
                        <span className="text-gray-500 text-14 leading-22 font-bold mb-6 block">
                          <PlainTextWrapper field={{ value: String(result.raw.cwpagetype) }} />
                        </span>
                      )}
                      {(result.raw.cwtitle as string) && (
                        <h2 id={`result-title-${result.uniqueId}`} className={resultTitle()}>
                          <a
                            href={result?.clickUri}
                            onClick={() => controller?.select()}
                            onMouseDown={() => controller?.select()}
                            className="leading-[34px] pb-2 my-6 lg:line-clamp-1 line-clamp-2"
                          >
                            <PlainTextWrapper field={{ value: result.raw.cwtitle as string }} />
                          </a>
                        </h2>
                      )}
                      {(result.raw.cwdescription as string) && (
                        <p className={classNames(resultDescription(), 'mt-6')}>
                          <RichTextWrapper
                            field={{
                              value:
                                (result?.raw?.cwdescription as string)?.substring(0, 200) +
                                ((result?.raw?.cwdescription as string)?.length > 200 ? '...' : ''),
                            }}
                          />
                        </p>
                      )}
                    </>
                  )}
                </article>
              </li>
            );
          })
        ) : (
          <NoResultsMessage />
        )
      ) : null}
    </ul>
  );
};

const ProviderContent = ({
  result,
  controller,
}: {
  result: Result;
  controller: InteractiveResult | null;
}) => {
  const { ProviderProxyImage, providerImageMobile, providerImageDesktop, resultTitle } =
    TAILWIND_VARIANTS();

  return (
    <div className="flex flex-col md:flex-row gap-y-8 gap-x-12">
      <div className="flex-grow">
        <h2 id={`result-title-${result.uniqueId}`} className={resultTitle()}>
          <a
            aria-label={`${result.raw.cwfirstname as string} ${result.raw.cwlastname as string}`}
            href={result?.clickUri}
            onClick={() => controller?.select()}
            onMouseDown={() => controller?.select()}
          >
            {result.raw.cwfirstname as string} {result.raw.cwlastname as string}
          </a>
        </h2>
        {Array.isArray(result?.raw?.cwspecialties) && (
          <span className="block mt-5 font-normal text-18 text-gray-900">
            <PlainTextWrapper
              field={{
                value: result.raw.cwspecialties.map((spec: string) => spec.trim()).join(', '),
              }}
            />
          </span>
        )}
        <div className="block md:hidden w-full md:min-w-[250px] md:w-[250px] mb-8">
          {result.raw.cwphoto ? (
            <ImageWrapper
              className={providerImageMobile()}
              field={{
                value: {
                  src: `${result.raw.cwphoto}`,
                  alt: result.raw.cwtitle as string,
                },
              }}
            />
          ) : (
            <div className={ProviderProxyImage()}>
              <SvgIcon
                icon="user"
                className="!h-[60px] !w-[60px] [&>path]:fill-darkblue-base"
                size="lg"
                aria-label="Provider Profile Video"
              />
            </div>
          )}
        </div>

        {(result.raw.cwacceptingnewpatients as string) && (
          <div
            className={`${(result.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? 'bg-utility-red-50' : 'bg-utility-green-50'} text-gray-900 font-nunitoSans text-16 font-normal leading-[31px] md:inline-flex flex items-start lg:mt-12 mt-4 px-8 py-3 rounded-[6px]`}
          >
            <SvgIcon
              icon={`${(result.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? 'xcircle' : 'check-circle'}`}
              className={`${(result.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? '[&>path]:fill-utility-red-700' : '[&>path]:fill-utility-green-700'} mt-2 mr-4 !h-[22px] !w-[22px]`}
              size="lg"
              aria-label={result.raw.cwacceptingnewpatients}
            />
            <span>{result.raw.cwacceptingnewpatients as string}</span>
          </div>
        )}
      </div>
      <div className="hidden md:block max-w-[160px] w-full">
        {result.raw.cwphoto ? (
          <ImageWrapper
            className={providerImageDesktop()}
            field={{
              value: {
                src: `${result.raw.cwphoto}`,
                alt: result.raw.cwtitle as string,
              },
            }}
          />
        ) : (
          <div className={ProviderProxyImage()}>
            <SvgIcon
              icon="user"
              className="!h-[80px] !w-[80px] [&>path]:fill-darkblue-base"
              size="lg"
              aria-label="Provider Profile Video"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const LocationContent = ({
  result,
  controller,
}: {
  result: Result;
  controller: InteractiveResult | null;
}) => {
  const { locationImage, locationDirectionsButton, resultTitle } = TAILWIND_VARIANTS();
  const { getDictionaryValue } = useDictionary();
  return (
    <div className="flex flex-col md:flex-row gap-y-8 gap-x-12">
      <div className="flex flex-col md:flex-row gap-8 flex-grow">
        <div className="flex-grow">
          <h2
            id={`result-title-${result.uniqueId}`}
            className={classNames('block w-full', resultTitle())}
          >
            <a
              href={result?.clickUri}
              onClick={() => controller?.select()}
              onMouseDown={() => controller?.select()}
            >
              <PlainTextWrapper field={{ value: result.raw.cwtitle as string }} />
            </a>
          </h2>

          <span className="block mt-5 font-normal text-16 md:text-18 text-gray-900">
            <PlainTextWrapper field={{ value: result.raw.cwpagetype as string }} />
          </span>
          <div className="flex gap-4 mt-12">
            <span className="inline-block text-orange-700">
              <SvgIcon icon={'map-pin'} size="lg" />
            </span>

            <div className="text-16">
              {(result.raw.distanceinmiles as number) && (
                <div className="text-gray-900">
                  <b>{(result.raw.distanceinmiles as number)?.toFixed(1)} miles</b>
                  <span className="text-gray-500 ml-2">
                    ({getDictionaryValue('EstimatedDistance')})
                  </span>
                </div>
              )}
              <LinkWrapper
                field={{
                  value: {
                    href: result.raw.cwaddressmapurl,
                  },
                }}
                aria-label={`${getDictionaryValue('GetDirections')} to ${
                  result.raw.cwtitle ||
                  getAddress({
                    address: result.raw.cwaddress1 as string,
                    city: (result.raw.cwcity as string) || '',
                    state: (result.raw.cwstate as string) || '',
                    zip: (result.raw.cwzip as string) || '',
                  })
                }`}
              >
                <span className="group block">
                  {(result.raw.cwaddress1 as string) && (
                    <span className="relative block group-hover:underline underline-offset-[6px] text-gray-900">
                      <PlainTextWrapper
                        field={{
                          value: getAddress({
                            address: result.raw.cwaddress1 as string,
                            city: (result.raw.cwcity as string) || '',
                            state: (result.raw.cwstate as string) || '',
                            zip: (result.raw.cwzip as string) || '',
                          }),
                        }}
                      />
                    </span>
                  )}

                  {(result.raw.cwaddressmapurl as string) && (
                    <span
                      className={classNames(
                        'inline-flex mt-4 md:mt-0 min-h-[48px] min-w-[48px] group-hover:underline underline-offset-[6px] text-gray-900',
                        locationDirectionsButton()
                      )}
                    >
                      <PlainTextWrapper
                        field={{
                          value:
                            (result.raw.cwaddressmaptext as string) ||
                            getDictionaryValue('GetDirections'),
                        }}
                      />
                      <SvgIcon
                        icon="external-link"
                        className="!h-[22px] ml-4 !w-[22px] [&>path]:text-gray-900"
                        size="lg"
                        aria-label={`${getDictionaryValue('GetDirections')}`}
                      />
                    </span>
                  )}
                </span>
              </LinkWrapper>
            </div>
          </div>
        </div>
      </div>

      <div className="min-w-full w-full sm:min-w-[250px] sm:w-[250px] order-1 md:order-2">
        <ImageWrapper
          field={{
            value: {
              src: result.raw.cwphoto as string,
              alt: result.raw.cwtitle as string,
            },
          }}
          className={locationImage()}
        />
      </div>
    </div>
  );
};

const BlogContent = ({
  result,
  controller,
}: {
  result: Result;
  controller: InteractiveResult | null;
}) => {
  const { resultTitle, resultDescription, blogImage } = TAILWIND_VARIANTS();

  return (
    <div className="flex flex-col md:flex-row gap-y-8 gap-x-12">
      <div className="flex flex-col flex-grow">
        {(result.raw.cwpagetype as string) && (result.raw.cwblogdate as string) && (
          <div className="flex flex-row gap-4 justify-between">
            <span className="text-gray-500 text-14 leading-22 font-bold">
              <PlainTextWrapper field={{ value: String(result.raw.cwpagetype) }} />
            </span>

            <span className="text-gray-900 text-14 leading-22 font-normal">
              <PlainTextWrapper field={{ value: formatDate(result?.raw?.cwblogdate as number) }} />
            </span>
          </div>
        )}
        {(result.raw.cwtitle as string) && (
          <h2 id={`result-title-${result.uniqueId}`} className={resultTitle()}>
            <a
              href={result?.clickUri}
              onClick={() => controller?.select()}
              onMouseDown={() => controller?.select()}
              className="leading-[34px] my-6 lg:line-clamp-1 line-clamp-2"
            >
              <PlainTextWrapper field={{ value: result.raw.cwtitle as string }} />
            </a>
          </h2>
        )}
        {(result.raw.cwdescription as string) && (
          <p className={resultDescription()}>
            <RichTextWrapper
              field={{
                value:
                  (result?.raw?.cwdescription as string)?.substring(0, 200) +
                  ((result?.raw?.cwdescription as string)?.length > 200 ? '...' : ''),
              }}
            />
          </p>
        )}
      </div>

      <div className="min-w-full w-full sm:min-w-[250px] sm:w-[250px] order-1 md:order-2">
        <ImageWrapper
          field={{
            value: {
              src: result.raw.cwheroimage as string,
              alt: result.raw.cwtitle as string,
            },
          }}
          className={blogImage()}
        />
      </div>
    </div>
  );
};

export const Default = withStandardComponentWrapper(GlobalSearch);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['bg-blue-50'],
    headingStyle: ['leading-58 font-bold text-gray-900 font-centuryGothic text-40 md:text-48'],
    titleLinkWrapperStyle: ['flex flex-col lg:flex-row items-start justify-between gap-2 md:gap-6'],
    containerStyle: ['max-w-screen-xxl mx-auto md:px-[80px] px-10'],
    ProviderProxyImage: [
      'bg-blue-100',
      'rounded-md',
      'mb-[26px]',
      'lg:w-[160px] lg:h-[200px]',
      'md:w-[160px] md:h-[200px]',
      'w-[100px] h-[125px]',
      'flex items-center justify-center',
    ],
    // Added slots for repeated/complex classNames
    searchInputWrapper: [
      'flex-1',
      'relative',
      'bg-white',
      'rounded-sm',
      'focus:outline-2',
      'focus:outline-blue-base',
    ],
    searchInput: [
      'w-full',
      'text-16',
      'md:text-18',
      'leading-28',
      'text-gray-900',
      'focus:outline-2',
      'focus:outline-blue-base',
      'border-2',
      'border-gray-100',
    ],
    searchClearButton: [
      'absolute',
      'right-4',
      'top-1/2',
      '-translate-y-1/2',
      'bg-gray-500',
      'text-white',
      'rounded-full',
      'w-[24px]',
      'h-[24px]',
      'flex',
      'items-center',
      'justify-center',
    ],
    searchSuggestionsList: [
      'absolute',
      'z-10',
      'top-[77px]',
      'max-h-[240px]',
      'bg-white',
      'left-0',
      'w-full',
      'border-2',
      'border-gray-100',
      'rounded-sm',
      'overflow-auto',
      'p-2',
    ],
    searchSuggestionItem: [
      'px-8',
      'py-5',
      'font-bold',
      'hover:bg-blue-50',
      'hover:underline',
      'hover:cursor-pointer',
    ],
    searchSuggestionItemActive: ['bg-blue-50', 'font-bold'],
    searchButton: [
      'px-12',
      'font-semibold',
      'text-white',
      'bg-blue-600',
      'hover:bg-blue-700',
      'mt-5',
      'md:mt-0',
      'w-full',
      'md:w-fit',
    ],
    resultListItem: ['flex', 'flex-col', 'gap-6', 'border-b', 'border-gray-100', 'py-[30px]'],
    resultTitle: [
      'text-24',
      'md:text-28',
      'leading-31',
      'font-bold',
      'text-blue-base',
      'underline-offset-[6px]',
      'font-centuryGothic',
      'hover:underline',
      'hover:text-darkblue-base',
      'active:underline',
      'active:text-darkblue-base',
    ],
    resultDescription: ['text-16', 'leading-24', 'text-gray-900'],
    locationImage: ['aspect-[3/2]', 'w-full', 'rounded-[8px]', 'object-cover'],
    blogImage: ['aspect-[250/143]', 'w-full', 'rounded-[8px]', 'object-cover'],
    providerImageMobile: [
      'w-[100px]',
      'aspect-[100/125]',
      'min-h-[125px]',
      'md:w-[270px]',
      'md:h-[270px]',
      'rounded-[8px]',
      'object-cover',
      'object-top',
    ],
    providerImageDesktop: ['w-full', 'aspect-[160/200]', 'rounded-[8px]', 'object-cover'],
    locationDirectionsButton: [
      'min-h-[48px]',
      'min-w-[48px]',
      'flex',
      'items-center',
      'justify-center',
      'rounded-md',
      'focus:outline-2',
      'focus:outline-blue-base',
      'transition',
      'duration-150',
    ],
  },
});
